import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { checkEmail as Check } from "../../store/auth/authThunk";
import { clearAlerts } from "../../store/alert/alertSlice";
import Layout from "../../layout";
import { NextButton } from "../../components/DizeeComponent/NextButton";
import TypeWriter from "../../components/OnBoard/Typewriter";
import styled from "styled-components";
import dizeeLogo from "../../assets/images/dizee logo.svg";
import AccessibilityWidget from "../../components/AccessibilityWidget";

const BaseInput = styled.input`
  background: transparent;
  border: none;
  font-size: 12px;
  padding: 0;
  color: white;
  height: 18px;
  line-height: 1.5;
  // margin: 10px 0 40px 0;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    outline: none;
    caret-color: white;
  }
`;



const Input = styled(BaseInput).attrs(props => ({
  'data-available': props.isAvailable,
  'data-recognized': props.isRecognized,
  isAvailable: undefined,
  isRecognized: undefined
}))`
  width: 100%;
  transition: all 0.3s ease;
  background: transparent;
  margin: 2px 0;
  position: relative;
  color: ${props => {
    if (!props['data-available'] || !props['data-recognized']) return 'white';
    return '#90EE90';
  }};

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }

  &:focus {
    outline: none;
    caret-color: white;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 350px;
  margin: 0 auto;
  padding-left: 35px;
  overflow: hidden;
  height: 100vh;
  position: relative;
  touch-action: none;
`;

const FormSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 80px;
  gap: 2px;
`;

const Footer = styled.div`
  position: fixed;
  bottom: 27px;
  width: 280px;
`;

const LogoImage = styled.img`
  width: 48px;
  height: auto;
  margin-bottom: 16px;
`;

const FooterText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  max-width: 280px;
  line-height: 1.5;
  
  a {
    color: rgba(255, 255, 255, 0.5);
    text-decoration: underline;
  }
`;

const ButtonContainer = styled.div`
  height: 40px;
  margin-top: 40px;
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  display: flex;
  align-items: center;
`;

const LoadingIndicator = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 10px;
  opacity: ${props => props.show ? 1 : 0};
  transition: opacity 0.3s ease;
`;

const Home = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const { checkemail, loading } = useSelector((state) => state.auth);
  const debounceRef = useRef(null);
  const lastCheckedEmail = useRef("");

  // Add this useEffect for scroll control
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    document.body.style.touchAction = 'none';
    
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
      document.body.style.height = 'auto';
      document.body.style.touchAction = 'auto';
    };
  }, []);

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Debounced email checking with smooth transitions
  useEffect(() => {
    dispatch(clearAlerts());
    
    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Hide buttons immediately when email changes
    setShowButtons(false);
    setIsCheckingEmail(false);

    // Only proceed if email is valid
    if (!isValidEmail(email) || !email.length) {
      lastCheckedEmail.current = "";
      return;
    }

    // Don't check if it's the same email we just checked
    if (email === lastCheckedEmail.current) {
      setShowButtons(true);
      return;
    }

    // Show checking state after a brief delay for UX
    const checkingTimeout = setTimeout(() => {
      setIsCheckingEmail(true);
    }, 300);

    // Debounce the actual email check
    debounceRef.current = setTimeout(() => {
      lastCheckedEmail.current = email;
      dispatch(Check(email)).then(() => {
        // Small delay before showing buttons for smooth transition
        setTimeout(() => {
          setIsCheckingEmail(false);
          setShowButtons(true);
        }, 500);
      }).catch(() => {
        setIsCheckingEmail(false);
        setShowButtons(true);
      });
    }, 800); // 800ms delay before checking

    return () => {
      clearTimeout(checkingTimeout);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [email, dispatch]);

  const resetEmail = () => {
    setEmail("");
    setShowButtons(false);
    setIsCheckingEmail(false);
    lastCheckedEmail.current = "";
  };

  const handleCreateAccount = (e) => {
    e.preventDefault();
    
    // Ensure email is valid and checked
    if (!isValidEmail(email) || isCheckingEmail || loading) {
      return;
    }

    // If we haven't checked this email yet, wait for the check
    if (email !== lastCheckedEmail.current) {
      return;
    }

    navigate(`/signup?email=${email}`);
  };

  const handleLogin = () => {
    // Ensure email is valid and checked
    if (!isValidEmail(email) || isCheckingEmail || loading) {
      return;
    }

    navigate("/login-otp", { state: { email } });
  };

  return (
    <Layout>
      <Container>
        <FormSection>
          <TypeWriter text={"Login or sign up to get started"} speed={45} />
          <form onSubmit={handleCreateAccount}>
            <Input
              placeholder={"Enter your email"}
              value={email}
              isAvailable={isValidEmail(email)}
              isRecognized={checkemail}
              onChange={(e) => setEmail(e.target.value)}
            />
            
            <LoadingIndicator show={isCheckingEmail}>
              Verifying email...
            </LoadingIndicator>

            <ButtonContainer show={showButtons && email.length > 0 && isValidEmail(email) && !isCheckingEmail && !loading}>
              {!checkemail ? (
                <NextButton 
                  type="submit"
                  disabled={isCheckingEmail || loading}
                >
                  Create a new account
                </NextButton>
              ) : (
                <div style={{ display: 'flex', width: '100%' }}>
                  <NextButton 
                    type="button" 
                    style={{ marginRight: '43px' }}
                    onClick={handleLogin}
                    disabled={isCheckingEmail || loading}
                  >
                    Login
                  </NextButton>
                  <NextButton 
                    type="button"
                    onClick={resetEmail}
                  >
                    Clear
                  </NextButton>
                </div>
              )}
            </ButtonContainer>
          </form>
        </FormSection>
        <Footer>
          <LogoImage src={dizeeLogo} alt="DIZEE" />
          <FooterText>
            By clicking "Next" you agree to the DIZEE TOS{'\n'}
            and Privacy Policy.
          </FooterText>
        </Footer>
      </Container>
      <AccessibilityWidget />
    </Layout>
  );
};

export default Home;
